import React from 'react';
import { Presentation, CheckCircle, Users, Target, Calendar, TrendingUp } from 'lucide-react';

export default function TradeShows() {
  const tradeShows = [
    { name: 'MAGIC Las Vegas', logo: 'https://logo.clearbit.com/magicfashionevents.com', description: 'Fashion & Apparel' },
    { name: 'White Label Expo', logo: 'https://logo.clearbit.com/whitelabelexpo.com', description: 'E-commerce & DTC' },
    { name: 'NRF Big Show', logo: 'https://logo.clearbit.com/nrf.com', description: 'Retail Innovation' },
    { name: 'Inspired Home Show', logo: 'https://logo.clearbit.com/inspiredhomeshow.com', description: 'Home & Housewares' },
    { name: 'NY NOW', logo: 'https://logo.clearbit.com/nynow.com', description: 'Lifestyle & Gift' },
    { name: 'Atlanta Apparel', logo: 'https://logo.clearbit.com/atlanta-apparel.com', description: 'Apparel & Accessories' }
  ];

  const framework = [
    {
      phase: 'Phase 1: Strategic Show Selection',
      duration: '4-6 weeks before show',
      activities: [
        'Trade show ROI analysis and selection criteria',
        'Buyer attendance verification and target list building',
        'Competitor booth reconnaissance and positioning strategy',
        'Budget allocation across booth, travel, samples, and collateral',
        'Show-specific goal setting (leads, orders, partnerships)'
      ]
    },
    {
      phase: 'Phase 2: Pre-Show Preparation',
      duration: '3-4 weeks before show',
      activities: [
        'Booth design that tells your brand story in 100 sq ft',
        'Product curation: show your winners, tease your pipeline',
        'Appointment scheduling with top 50 target buyers',
        'Staff training on qualification, pitch, and closing',
        'Digital campaign driving booth traffic and appointments'
      ]
    },
    {
      phase: 'Phase 3: Show Execution',
      duration: 'Show dates (3-5 days)',
      activities: [
        'Professional booth staffing and lead capture',
        'Live product demonstrations and storytelling',
        'On-the-spot order writing and follow-up scheduling',
        'Competitive intelligence gathering',
        'Evening networking events and private appointments'
      ]
    },
    {
      phase: 'Phase 4: Post-Show Conversion',
      duration: '2-4 weeks after show',
      activities: [
        'Lead scoring and prioritization within 48 hours',
        'Personalized follow-up sequences for each lead tier',
        'Sample fulfillment and line sheet distribution',
        'Order conversion tracking and pipeline management',
        'Show ROI analysis and learnings documentation'
      ]
    }
  ];

  const differentiators = [
    {
      title: 'The 10-30-60 Method',
      icon: Target,
      description: 'We target 10 must-win accounts, 30 high-probability buyers, and 60 qualified prospects per show—ensuring focused execution instead of spray-and-pray booth sitting.'
    },
    {
      title: 'Appointment Pre-Booking',
      icon: Calendar,
      description: 'We pre-schedule 40-60% of your show appointments before doors open, dramatically increasing face-time with decision makers and reducing dead booth hours.'
    },
    {
      title: 'Professional Representation',
      icon: Users,
      description: 'Our experienced brand ambassadors understand retail buying dynamics, speak the language of buyers, and can close orders on your behalf—not just collect business cards.'
    },
    {
      title: 'Post-Show Strike Force',
      icon: TrendingUp,
      description: 'Within 48 hours of show close, we deploy aggressive follow-up sequences. 73% of trade show leads die from lack of timely follow-up—we convert them into orders.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center">
              <Presentation size={32} className="text-white" />
            </div>
            <div>
              <h1 className="text-5xl font-bold text-[#000000]">
                Trade Show Representation
              </h1>
              <p className="text-xl text-gray-600 mt-2">
                Professional presence at major industry events
              </p>
            </div>
          </div>
        </div>

        {/* Where We Show Up */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-[#000000]">We Maintain Regular Presence At</h2>
          <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8">
            <p className="text-lg text-gray-700 mb-8">
              Our team attends the industry's most influential trade shows, providing our brand partners with professional representation and direct access to qualified buyers.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {tradeShows.map((show, index) => (
                <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#38A3A5] transition-all">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4 h-16 flex items-center justify-center">
                      <img 
                        src={show.logo} 
                        alt={show.name}
                        className="h-12 w-auto object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div style={{display: 'none'}} className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                        <Presentation size={24} className="text-gray-400" />
                      </div>
                    </div>
                    <h3 className="font-bold text-lg text-gray-800 mb-2">{show.name}</h3>
                    <p className="text-sm text-gray-600">{show.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* The Motif Unbound Framework */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">The Trade Show Maximum ROI Framework</h2>
          <p className="text-lg text-gray-700 mb-8">
            Most brands waste $50,000+ on trade shows with disappointing results: tired staff, random conversations, business cards that go nowhere. We execute trade shows as precision sales campaigns with measurable ROI. Here's our systematic approach:
          </p>
          
          <div className="space-y-6">
            {framework.map((phase, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xl">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-[#000000] mb-2">{phase.phase}</h3>
                    <p className="text-[#38A3A5] font-semibold mb-4">Timeline: {phase.duration}</p>
                  </div>
                </div>
                <ul className="space-y-3 ml-16">
                  {phase.activities.map((activity, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-[#38A3A5] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{activity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* What Makes Us Different */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">What Makes Us Different</h2>
          <p className="text-lg text-gray-700 mb-8">
            Anyone can rent booth space and hand out brochures. We treat trade shows as strategic business development campaigns with pre-planned targets, disciplined execution, and aggressive follow-through that converts leads into revenue.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {differentiators.map((item, index) => (
              <div key={index} className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#000000]">{item.title}</h3>
                </div>
                <p className="text-gray-700">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Results Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-[#000000]">Proven Results</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-8 text-white">
              <div className="text-5xl font-bold mb-2">4.2x</div>
              <div className="text-lg opacity-90">Average ROI on trade show investment</div>
            </div>
            <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-8 text-white">
              <div className="text-5xl font-bold mb-2">58%</div>
              <div className="text-lg opacity-90">Pre-booked appointments before show opens</div>
            </div>
            <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-8 text-white">
              <div className="text-5xl font-bold mb-2">180+</div>
              <div className="text-lg opacity-90">Qualified buyer conversations per show</div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Ready to Maximize Your Trade Show ROI?</h2>
          <p className="text-xl mb-8 opacity-90">
            Let's discuss which shows align with your growth strategy and how we can represent your brand professionally.
          </p>
          <a 
            href="mailto:contact@motifunbound.com?subject=Schedule%20a%20Consult&body=Hi%20Motif%20Unbound%20team,%0D%0A%0D%0AI'd%20like%20to%20schedule%20a%20consultation%20to%20discuss%20our%20market%20expansion.%20Please%20share%20available%20times.%0D%0A%0D%0AThank%20you."
            className="inline-block bg-white text-[#38A3A5] px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all"
          >
            Schedule a Consult
          </a>
        </div>
      </div>
    </div>
  );
}
